import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "@geoman-io/leaflet-geoman-free";
import "@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css";
import FreeDraw from "leaflet-freedraw";
import "leaflet-path-drag";
import "leaflet.glify";
import { faSquare } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    Box,
    Alert,
    Typography,
    CircularProgress,
    Menu,
    MenuItem,
    Tooltip,
    Snackbar,
    TextField,
    IconButton,
    Slider,
    Paper,
    Button,
    Modal,
    LinearProgress
} from "@mui/material";
import { FaChevronLeft } from "react-icons/fa"
import * as turf from "@turf/turf";
import { v4 as uuidv4 } from 'uuid'
import { MdOutlineMyLocation, MdLocationSearching } from "react-icons/md"
import { GrFormViewHide, GrFormView } from "react-icons/gr"
import { MdOutlineClose } from "react-icons/md"

import "../../styles/css/viewer.css";
import { Restercoods } from "../../utils/rastercoods";
import { get, post, put, customPost } from "../../utils/http";
import { defaultMarkerColor } from "../../styles/js/colors.style";
import {
    getSysAnnoData,
    getModifiedGSData,
    getXmlFile,
    processXMLMarkers,
    calculateDistance,
    mergePolyList,
    drawHeatMap,
    processBcrHeatData,
    getHeatColor,
    processGeneHeatData,
    getJsonFile,
    processJsonHeatmap,
    processPredictionResults,
} from "./helper/processAnnotations";
import {
    AnnotationClassModal,
    MapWarningTexts,
} from "./helper/uiElements";
import ImageSideBar from "./helper/sideBar";
import {
    getMapProps,
    cachedFallbackGetLayer,
    cachedFallbackPostLayer,
} from "./helper/mapProps";
import { sortListAlphaNumeric } from "../../utils/helperFunc";
import { modalStyle } from "../../styles/js/common";
import { tileApiKey, tileAppUrl } from "../../utils/config";
import { ViewerContext } from "./helper/context";
import { handleAnnoCheckbox } from "./helper/common";
import {
    GeoJSON,
    GenomicDataObj,
    FileNodeProp,
    Marker,
    Annotation,
    Category,
    TypeVersion,
    Version,
    PredictionResponse,
    ColorLibObj,
    ImageData,
    MapPoint,
    NodeLeaf,
    HeatMaps
} from "../../utils/types";
import TouchToMouseMapper from "./helper/Touch2Mouse";
import { LoadingAnimeSpinner } from "../common/Loaders";
import Versions from "./helper/Versions";

let editLayer: { [x: number]: number } = {};
let xmlPolygonData: { [x: string]: GeoJSON } = {};
let genomicData: { [x: string]: GenomicDataObj } = {};
let heatmapData: { [x: string]: {[y: string]: HeatMaps} } = {};

let editTool: boolean = false;
let removeLayer: boolean = false;
let multiSelect: boolean = false;
let freeDrawEnabled: boolean = false;
let dragMarkerEnabled: boolean = false;
let drawPin: boolean = false;

let imageWidth: number = 0;
let imageHeight: number = 0;
let imageZoomLvl: number = 0;

let webGLPolygons: any[] = [];

let heatMapOverlay: any = null;
let mapCoords: any = null;
let imageOverlay: any = null;
let tileCache: any = null;
let touchMapper: any = null

interface ImageViewerProps {
    imageProp: FileNodeProp
    resetMap: Function
    readonly?: boolean
}

const ImageViewer: React.FC<ImageViewerProps> = ({ imageProp, resetMap, readonly }) => {
    const mapRef = useRef<HTMLDivElement>(null);
    const mapInstance = useRef<any>(null)
    const userAnnoRef = useRef<boolean>(false);
    const ongoingXmlReq = useRef(new Set());
    const tileOverlayLayer = useRef<any>(null)
    const freeDrawRef = useRef<any>(null)
    const bcrHeatmap = useRef<any>(null)

    const [sysGsAnnotations, setSysGsAnnotations] = useState<Marker[]>([]);
    const [modifiedGsAnnotations, setModifiedGsAnnotations] = useState<Annotation[]>([]);
    const [versions, setVersions] = useState<TypeVersion[]>([]);
    const [multiSelectList, setMultiSelectList] = useState<any[]>([]);
    const [copiedMarkerList, setCopiedMarkerList] = useState<any[]>([]);
    const [sysClassFilter, setSysClassFilter] = useState<string[]>([]);
    const [userFilter, setUserFilter] = useState<number[]>([]);
    const [bcrClassFilter, setBCRClassFilter] = useState<string[]>([]);

    const [xmlNames, setXmlNames] = useState<string[]>([]);
    const [xmlClassFilter, setXmlClassFilter] = useState<{[x: string]: string[]}>({});

    const [gsPredictionResult, setGsPredictionResult] = useState<PredictionResponse | {}>({});
    const [xmlClasses, setXmlClasses] = useState<{ [x: string]: Category[] }>({});
    const [jsonClasses, setJsonClasses] = useState<{ [x: string]: NodeLeaf[] }>({});
    const [selectedVersion, setSelectedVersion] = useState<number>(0)
    const [activeVerId, setActiveVerId] = useState<number>(0)
    const [sysMarkerVer, setSysMarkerVer] = useState<number>(0)
    const sysMarkerVerRef = useRef(sysMarkerVer)
    const [tileInputCoord, setTileInputCoord] = useState<{ x: number | null, y: number | null }>({ x: null, y: null });

    const [toast, setToast] = useState<string>('')

    const [imageError, setImageError] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [addNewTag, setAddNewTag] = useState<boolean>(false);
    const [isMarkersLoading, setIsMarkersLoading] = useState<boolean>(false);
    const [sysAnnoLoader, setSysAnnoLoader] = useState<boolean>(false);
    const [userAnnoLoader, setUserAnnoLoader] = useState<boolean>(false);
    const [waitingForRes, setWaitingForRes] = useState<boolean>(false);
    const [mapError, setMapError] = useState<boolean>(false);
    const [serverError, setServerError] = useState<string>('');
    const [showClassMenu, setShowClassMenu] = useState<boolean>(false);
    const [deleteModal, setDeleteModal] = useState<boolean>(false);
    const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
    const [isMarkerSelected, setIsMarkerSelected] = useState<boolean>(false);
    const [polyCreateTools, setPolyCreateTools] = useState<boolean>(false);
    const [showSlideFilters, setShowSlideFilters] = useState<boolean>(false);
    const [showMapCoords, setShowMapCoords] = useState<boolean>(false);
    const [showLiveMeasurement, setShowLiveMeasurement] = useState<boolean>(false);
    const [tileInput, setTileInput] = useState<boolean>(false);

    const [imageData, setImageData] = useState<ImageData | null>(null);
    const [colorLib, setColorLib] = useState<ColorLibObj[]>([]);
    const [renderAnnoType, setRenderAnnoType] = useState<string | null>(null);
    const [bcrAnnotations, setBcrAnnotations] = useState<GeoJSON | null>(null);
    const [BCRScore, setBCRScore] = useState<number | null>(null);
    const [tileMsg, setTileMsg] = useState<any>(null);
    const [selectedMarker, setSelectedMarker] = useState<any | null>(null);
    const [selectedPoint, setSelectedPoint] = useState<MapPoint | null>(null);
    const [contextMenuPos, setContextMenuPos] = useState<{ mouseY: number, mouseX: number } | null>(null);
    const [jsonName, setJsonName] = useState<string>('');
    const [jsonClassFilter, setJsonClassFilter] = useState<string>('');

    const [bcrHeatmapLoading, setBcrHeatmapLoading] = useState<boolean>(false);

    const [brightnessValue, setBrightnessValue] = useState<number>(100);
    const [contrastValue, setContrastValue] = useState<number>(100);
    // const [previousZoom, setPreviousZoom] = useState<number>(0)
    // const [currentZoom, setCurrentZoom] = useState<number>(0)

    const [localEditPointer, setLocalEditPointer] = useState<number>(0)
    const [localEditHistoryIds, setLocalEditHistoryIds] = useState<number[]>([])
    const localEditHistoryIdsRef = useRef(localEditHistoryIds)

    const selectedLayerById = new Map(multiSelectList.map(layer => [String(layer.properties.id), layer]))
    const MAX_BULK_SELECTION = 2500

    const userId: number = Number(localStorage.getItem("user_id") || "");

    const closeMapContextMenu = () => {
        setShowClassMenu(false);
        setContextMenuPos(null);
    };

    const handleMenuContextEvent = (e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();
        setContextMenuPos(null);
    };

    const closeServerError = () => setServerError('');

    const handleNewTagClose = () => setAddNewTag(false);

    const handleMapContextEvnt = (e: React.MouseEvent<HTMLElement>) =>
        e.preventDefault();

    const handleMapWheelEvnt = () => closeMapContextMenu();

    const handleCopyAnno = () => {
        try {
            setCopiedMarkerList([selectedMarker]);
        } catch (err) {
            console.log(err);
        } finally {
            setContextMenuPos(null);
        }
    };

    const handleBringDownMarker = () => {
        try {
            selectedMarker.bringToBack();
        } catch (err) {
            console.log(err);
        } finally {
            setContextMenuPos(null);
        }
    };

    const isSelectionInsideMarker = () => {
        try {
            if (!selectedPoint || !selectedMarker || !selectedMarker.properties) return false

            const { type } = selectedMarker.properties
            const px = selectedPoint.lat
            const py = selectedPoint.lng

            if (type==='pin') {
                const pinPoint = selectedMarker.getLatLng()
                if (!pinPoint) return false
                return pinPoint.lat===px && pinPoint.lng===py
            } else if (type==='polygon') {
                const polyPoints = selectedMarker.getLatLngs()[0]

                if (!polyPoints || polyPoints.length < 3) return false
                    
                let inside = false
                for (let i = 0, j = polyPoints.length - 1; i < polyPoints.length; j = i++) {
                    const xi = polyPoints[i].lat, yi = polyPoints[i].lng
                    const xj = polyPoints[j].lat, yj = polyPoints[j].lng
                    const intersect = ((yi > py) !== (yj > py)) && (px < (xj - xi) * (py - yi) / (yj - yi) + xi)
                    if (intersect) {
                        inside = true
                        break
                    }
                }
                return inside
            } else if (type==='line') {
                const linePoints = selectedMarker.getLatLngs()[0]
                if (linePoints.length!==2) return false

                const [p1, p2] = linePoints
                const cross = (py - p1.lng) * (p2.lat - p1.lat) - (px - p1.lat) * (p2.lng - p1.lng)
                if (Math.abs(cross) > 1e-2) return false
                const dot = (px - p1.lat) * (px - p2.lat) + (py - p1.lng) * (py - p2.lng)
                return dot <= 0
            } else {
                return false
            }
        } catch (err) {
            console.log(err)
            return false
        }
    }

    useEffect(() => {
        // hide marker contextmenu if selection on map is not on any marker
        if (selectedPoint && selectedMarker) setIsMarkerSelected(isSelectionInsideMarker())
    }, [selectedPoint]); // eslint-disable-line

    const calcNewPos = (marker: any, position: MapPoint) => {
        // Convert marker's vertices, center & target position from latlng to XY
        // Using latlng will change the height of the shape when moving up and down due to the spherical shape of the map
        const newPos = mapCoords.project([position.lat, position.lng]);
        const points = marker.getLatLngs()[0].map((vert: any) => mapCoords.project([vert.lat, vert.lng]));
        let center = marker.getBounds().getCenter();
        center = mapCoords.project([center.lat, center.lng]);
        // Find the offset between its center and vertices
        const offsets = [];
        for (let i = 0; i < points.length; i++) {
            const point = [];
            point.push(points[i].x - center.x);
            point.push(points[i].y - center.y);
            offsets.push(point);
        }
        // Create new vertices in new position based on the offset values
        const newPoints = [];
        for (let i = 0; i < offsets.length; i++)
            newPoints.push({X: newPos.x + offsets[i][0], Y: newPos.y + offsets[i][1]});
        return newPoints;
    };

    const handlePasteAnno = () => {
        try {
            if (selectedPoint) {
                copiedMarkerList.forEach((marker) => {
                    const coordinates = calcNewPos(marker, selectedPoint);
                    setContextMenuPos(null);
                    createPolyHandler(coordinates, null, "poly", marker.properties.category);
                });
            }
        } catch (err) {
            console.log(err);
        }
    };

    const handleShowClassList = () => setShowClassMenu(true);

    const removeFromAnnoClassLibrary = (classId: number) => {
        try {
            const tempColors = colorLib.filter((i) => i.id !== classId);
            setColorLib(tempColors);
        } catch (err) {
            console.log(err);
        }
    };

    const updateFromAnnoClassLibrary = (classData: ColorLibObj) => {
        try {
            const tempColors = colorLib;
            const index = tempColors.findIndex((i) => i.id === classData.id);
            if (index === -1) tempColors.push(classData);
            else tempColors[index] = classData;
            setColorLib(tempColors);
        } catch (err) {
            console.log(err);
        }
    };

    const updateAnnoClassLibrary = async () => {
        try {
            const res = await get("Annotationlibs/");
            if (res.length) setColorLib(sortListAlphaNumeric(res));
        } catch (err) {
            console.log(err);
        }
    };

    const toggleEditTool = () => {
        editTool = !editTool;
        if (!editTool) {
            setSelectedMarker((prevMarker: any) => {
                if (prevMarker && prevMarker.properties.id in editLayer) prevMarker.pm.toggleEdit({ allowSelfIntersection: false });
            });
            editLayer = {};
        }
    };

    const toggleRemovePolygon = () => (removeLayer = !removeLayer);

    const closeDeleteModal = () => {
        if (removeLayer) document.querySelector<HTMLElement>(".leaflet-pm-icon-delete")?.click()
        setDeleteModal(false);
    };

    // To-Do
    const showLiveCoordsEdit = (event: any) => { };

    const createPolygon = (polyData: Annotation, allowEdit: boolean, type: string) => {
        try {
            if (polyData.is_deleted || !Array.isArray(polyData.coordinates) || !polyData.category) return;

            let color = defaultMarkerColor;
            try {
                if (type === "xml" && polyData.color) color = polyData.color;
                else throw Error("Use colorlib");
            } catch {
                const cat_col = colorLib.find((y) => y.name.toLowerCase() === polyData.category!.toLowerCase());
                color = cat_col ? cat_col.color : defaultMarkerColor;
            }
            
            // Process the main outer boundary
            const points = polyData.coordinates.map((vert) => mapCoords.unproject([vert.X, vert.Y]));

            // Setup Leaflet multi-dimensional array for holes
            const polygonLatLngs: any[] = [points];

            // Process holes if they exist
            if (polyData.holes && Array.isArray(polyData.holes)) {
                polyData.holes.forEach((holeCoords: any) => {
                    const holePoints = holeCoords.map((vert: any) => mapCoords.unproject([vert.X, vert.Y]));
                    polygonLatLngs.push(holePoints);
                });
            }

            const poly = L.polygon(polygonLatLngs, {color: color, draggable: allowEdit}).addTo(mapInstance.current);

            L.DomEvent.fakeStop = function () { };  // L.DomEvent.fakeStop is not a function fix
            L.DomEvent.skipped = function () { };   // L.DomEvent.skipped is not a function fix

            if (poly.dragging) poly.dragging.disable();
            poly.properties = {
                id: polyData.id,
                category: polyData.category,
                coordinates: polyData.coordinates,
                holes: polyData.holes || [],
                area: polyData.area,
                type: type,
                color: color
            };

            let area = null;
            if ("area" in polyData && polyData.area && !isNaN(polyData.area)) {
                area = `${polyData.area / 16000000 !== 0 && polyData.area / 16000000 < 0.01
                    ? (polyData.area / 16000000).toFixed(1 - Math.floor(Math.log(polyData.area / 16000000) / Math.log(10)))
                    : (polyData.area / 16000000).toFixed(2)
                } mm²`;
            }
            const tooltipText = area
                ? `${polyData.category.length <= 4
                    ? polyData.category.toUpperCase()
                    : polyData.category
                } <wbr>Area: ${area}`
                : polyData.category;
            poly.bindTooltip(tooltipText, {
                permanent: false,
                direction: "center",
                className: "marker-popup",
            });

            poly.on("mouseover", (e: any) => {
                if (!multiSelect) e.target.setStyle({ color: "red", fillColor: "red" });
                if (dragMarkerEnabled && e.target.dragging && allowEdit) e.target.dragging.enable();
            });
            poly.on("mouseout", (e: any) => {
                if (!multiSelect) e.target.setStyle({ color: e.target.properties.color, fillColor: e.target.properties.color });
                if (dragMarkerEnabled && e.target.dragging && allowEdit) e.target.dragging.disable();
            });

            if (allowEdit) {
                poly.on("click", (e: any) => {
                    // For touch screens, select and drag since hover is not supported.
                    if (dragMarkerEnabled && e.target.dragging) e.target.dragging.enable();

                    setSelectedMarker((prevMarker: any) => {
                        if (prevMarker && editTool && prevMarker.properties.id in editLayer) prevMarker.pm.toggleEdit({ allowSelfIntersection: false });
                        return e.target;
                    });
                    if (editTool) {
                        if (e.target.properties.id in editLayer) editLayer[e.target.properties.id] += 1;
                        else editLayer = { [e.target.properties.id]: 0 };
                        showLiveCoordsEdit(e);
                        e.target.pm.toggleEdit({ allowSelfIntersection: true });
                        if (editLayer[e.target.properties.id] > 0) editPolygonHandler(e.target);
                    }
                    if (removeLayer) setDeleteModal(true);
                    if (multiSelect) {
                        setMultiSelectList((prevState) => {
                            const index = prevState.findIndex(i => i?.properties?.id === e.target.properties.id);
                            if (index !== -1) {
                                e.target.setStyle({ fillColor: e.target.properties.color, color: e.target.properties.color });
                                return [...prevState.filter(i => i?.properties?.id !== e.target.properties.id)];
                            } else {
                                e.target.setStyle({ fillColor: "red", color: "red" });
                                return [...prevState, e.target];
                            }
                        });
                    }
                });
                if (allowEdit) {
                    poly.on("contextmenu", (e: any) => {
                        setSelectedMarker(e.target);
                        setIsMarkerSelected(true);
                    });
                    poly.on("dblclick", (e: any) => {
                        setSelectedMarker(e.target);
                        setIsMarkerSelected(true);
                    });
                    poly.on("dragend", (e: any) => {
                        if (freeDrawRef.current) disableFreeDraw() // Fix disabled map dragging after drag edit
                        editPolygonHandler(e.target);
                    });
                }
            }
        } catch (err) {
            console.log(err);
        }
    };

    const createLines = (lineData: Annotation) => {
        try {
            if (lineData.is_deleted || !Array.isArray(lineData.coordinates)) return;

            const distance = lineData.distance || calculateDistance(lineData.coordinates)
            const points = lineData.coordinates.map((vert: any) => mapCoords.unproject([vert.X, vert.Y]));
            
            const Line = new L.Polyline(points, {
                color: "black",
                weight: 4,
                opacity: 0.5,
                smoothFactor: 1,
            }).addTo(mapInstance.current);
            Line.properties = {
                id: lineData.id,
                distance: distance,
                coordinates: lineData.coordinates,
                points: points,
                type: "line",
            };
            Line.on("click", (e: any) => {
                setSelectedMarker(e.target);
                if (removeLayer) setDeleteModal(true);
            });
            Line.on("mouseover", function (this: typeof Line, e: any) {
                if (!multiSelect) this.setStyle({color: 'red'});
            });
            Line.on("mouseout", function (this: typeof Line, e: any) {
                if (!multiSelect) this.setStyle({color: 'black'});
            });
            Line.on("contextmenu", (e: any) => {
                setSelectedMarker(e.target);
                setIsMarkerSelected(true);
            });
            Line.on("dblclick", (e: any) => {
                setSelectedMarker(e.target);
                setIsMarkerSelected(true);
            });
            Line.bindTooltip(`${(distance / 4000).toFixed(3).toString()} mm`, {
                permanent: true,
                direction: "center",
                className: "marker-popup",
            });
        } catch (err) {
            console.log(err);
        }
    };

    const createCircleMarker = (markerData: Annotation) => {
        try {
            if (markerData.is_deleted || Array.isArray(markerData.coordinates)) return;

            const point = mapCoords.unproject([
                markerData.coordinates.X,
                markerData.coordinates.Y,
            ]);
            const pin = new L.circleMarker(point, {
                radius: 7,
                color: "blue",
                fillColor: "blue",
                fillOpacity: 0.2,
            }).addTo(mapInstance.current);
            pin.properties = {
                id: markerData.id || '0',
                coordinates: markerData.coordinates,
                point: point,
                type: "pin",
            };
            pin.on("mouseover", (e: any) => {
                if (!multiSelect) e.target.setStyle({ fillColor: "red", color: "red" });
            });
            pin.on("mouseout", (e: any) => {
                if (!multiSelect) e.target.setStyle({ fillColor: "blue", color: "blue" });
            });
            pin.on("click", (e: any) => {
                setSelectedMarker(e.target);
                if (removeLayer) setDeleteModal(true);
            });
            pin.on("contextmenu", (e: any) => {
                setSelectedMarker(e.target);
                setIsMarkerSelected(true);
            });
            pin.on("dblclick", (e: any) => {
                setSelectedMarker(e.target);
                setIsMarkerSelected(true);
            });
            return pin;
        } catch (err) {
            console.log(err);
            return null;
        }
    };

    const createPolyHandler = async (
        coords: any,
        tempPoly: any,
        markerType: any,
        markerClass: any
    ) => {
        setIsMarkersLoading(true);
        const tempMarker = markerType !== "pin" ? tempPoly : createCircleMarker({id: '0', coordinates: coords});
        try {
            if (markerType === "poly") {
                // draw polygons
                const payload = {
                    coordinates: coords,
                    user_id: userId,
                    image_id: imageData!.id,
                    type: markerType,
                    category: userAnnoRef.current ? "Cancer" : markerClass,
                    base_version: sysMarkerVerRef.current
                };
                const res = await post(`Annotations/image/${imageData!.id}`, payload);
                if (tempPoly) mapInstance.current.removeLayer(tempPoly);
                const newPoly = res.marker;
                const points = newPoly.coordinates.map((vert: any) => mapCoords.unproject([vert.X, vert.Y]));
                newPoly.points = points;
                newPoly.color = defaultMarkerColor;
                createPolygon(newPoly, true, "polygon");
                // update category list, prediction info, version info and modified annotation list
                updateModifiedAnnoInList(res.marker);
                setGsPredictionResult(processPredictionResults(res.prediction_result, res.categories));
                updateCurrentEdits(res.id, res.sys_marker_ver);
            } else if (markerType === "line") {
                // draw line
                const distance = calculateDistance(coords);
                const payload = {
                    coordinates: coords,
                    user_id: userId,
                    image_id: imageData!.id,
                    type: markerType,
                    distance: distance,
                    base_version: sysMarkerVerRef.current
                };
                const res = await post(`Annotations/image/${imageData!.id}`, payload);
                if (tempPoly) mapInstance.current.removeLayer(tempPoly);
                const newLine = res.marker;
                const points = newLine.coordinates.map((vert: any) =>
                    mapCoords.unproject([vert.X, vert.Y])
                );
                newLine.points = points;
                createLines(newLine);
                // update category list, prediction info, version info and modified annotation list
                updateModifiedAnnoInList(res.marker);
                setGsPredictionResult(processPredictionResults(res.prediction_result, res.categories));
                updateCurrentEdits(res.id, res.sys_marker_ver);
            } else if (markerType === "pin") {
                // Draw pin
                const payload = {
                    coordinates: coords,
                    user_id: userId,
                    image_id: imageData!.id,
                    type: "pin",
                    base_version: sysMarkerVerRef.current
                };
                const res = await post(`Annotations/image/${imageData!.id}`, payload);
                // Update version info and modified annotation list. Pin doesn't affect prediction result or category
                if (tempMarker) mapInstance.current.removeLayer(tempMarker);
                createCircleMarker(res.marker);
                updateModifiedAnnoInList(res.marker);
                updateCurrentEdits(res.id, res.sys_marker_ver);
            }
        } catch (err) {
            console.log(err);
            if (tempMarker) mapInstance.current.removeLayer(tempMarker);
        } finally {
            setIsMarkersLoading(false);
            editLayer = {};
        }
    };

    const cleanUp = () => {
        editTool = false;
        editLayer = {};
        removeLayer = false;
        imageWidth = 0;
        imageHeight = 0;
        multiSelect = false;
        freeDrawEnabled = false;
        disableFreeDraw()
        freeDrawRef.current = null;
        xmlPolygonData = {};
        heatMapOverlay = null;
        mapCoords = null;
        webGLPolygons = [];
        imageOverlay = null;
        dragMarkerEnabled = false;
        drawPin = false;
        genomicData = {};
        heatmapData = {}
        if (mapInstance.current) {
            mapInstance.current.remove();
            mapInstance.current = null;
        }
        if (tileCache) tileCache.clear();
    };

    const initMap = async () => {
        try {
            if (!mapRef.current) return

            const { url, maxZoom, slide_width, slide_height, customTileData } = await getMapProps(imageData!);

            imageZoomLvl = maxZoom;
            imageWidth = slide_width;
            imageHeight = slide_height;

            const tempMap = L.map(mapRef.current, {
                crc: L.CRS.Simple,
                minZoom: 0,
                maxZoom: imageZoomLvl,
                preferCanvas: true,
                zoomSnap: 0,
                zoomDelta: 0.25,
                wheelPxPerZoomLevel: 90,
                zoomAnimation: true,
                zoomAnimationThreshold: 4,
                fadeAnimation: false,
                markerZoomAnimation: false,
                inertia: true,
                inertiaDeceleration: 3000,
                inertiaMaxSpeed: 1500,
            });

            touchMapper = new TouchToMouseMapper(tempMap)

            mapCoords = new Restercoods(tempMap, [imageWidth, imageHeight]);
            mapCoords.setMaxBounds();

            // //----------Map outline--------------------
            // const coordinates = [{ X: 0, Y: 0}, { X: imageWidth, Y: 0}, { X: imageWidth, Y: imageHeight}, { X: 0, Y: imageHeight}]
            // const points = coordinates.map(vert => mapCoords.unproject([vert.X, vert.Y]))
            // L.polygon([points], { color: 'grey', draggable: false, fillColor: "none" }).addTo(tempMap)
            // //------------------------------------------

            let tileLayer;
            if (customTileData) {
                tileLayer = cachedFallbackPostLayer(customTileData, imageZoomLvl)
            } else {
                ({ tileLayer, tileCache } = cachedFallbackGetLayer(url, imageZoomLvl));
            }
            if (!tileLayer) throw Error("Failed to create tile layer");
            tileLayer.addTo(tempMap);

            // json data -> tiff tile overlay
            const secondLayer = L.tileLayer("", {
                maxZoom: imageZoomLvl,
                noWrap: true,
                opacity: 0.6,
            });
            tileOverlayLayer.current = secondLayer
            L.control.layers(
                { Slide: tileLayer },
                { Overlay: secondLayer },
                { position: "bottomright" }
            ).addTo(tempMap);

            tempMap.setView([0, 0], 2);
            tempMap.setMaxBounds(null); // drag the map freely without boundary lock

            tempMap.pm.addControls({
                position: "topleft",
                drawMarker: false,
                drawRectangle: !readonly && (imageData!.status !== "Delete") ? true : false,
                drawPolygon: !readonly && (imageData!.status !== "Delete") ? true : false,
                drawText: false,
                drawCircle: false,
                cutPolygon: false,
                editMode: false,
                dragMode: false,
                removalMode: false,
                drawCircleMarker: false,
                rotateMode: false,
                drawPolyline: false,
            });
            // tempMap.pm.disableGlobalDragMode()

            // polygon edit tool
            if (!readonly) {
                tempMap.pm.Toolbar.createCustomControl({
                    name: "editpolygon",
                    title: "Edit",
                    onClick: () => toggleEditTool(),
                    className: "leaflet-pm-icon-edit",
                });
            }

            // polygon delete tool
            if (!readonly) {
                tempMap.pm.Toolbar.createCustomControl({
                    name: "remove",
                    title: "Remove",
                    onClick: () => toggleRemovePolygon(),
                    className: "leaflet-pm-icon-delete",
                });
            }

            // Snap Slide tool
            tempMap.pm.Toolbar.createCustomControl({
                name: "center-map",
                block: "custom",
                title: "Center Slide",
                toggle: false,
                onClick: () => tempMap.flyTo(mapCoords.unproject([imageWidth / 2, imageHeight / 2]), 1),
                className: "icon-snap-slide",
            });

            // Brightness, contrast sliders tool
            tempMap.pm.Toolbar.createCustomControl({
                name: "filter",
                block: "custom",
                title: "Filter",
                className: "icon-filters",
                onClick: () => setShowSlideFilters((prevState) => !prevState),
            });
            // tempMap.on("zoomstart", () => setPreviousZoom(tempMap.getZoom()));
            // tempMap.on("zoomend", () => setCurrentZoom(tempMap.getZoom()));
            mapInstance.current = tempMap;

            initPolyCreationTools()

            // prevent showing toolbar before the annotations are loaded
            const checkAllAnno = document.querySelector<HTMLInputElement>("#check-all-anno")
            if (checkAllAnno) checkAllAnno.checked = true

            await getVersion()
        } catch (err) {
            console.log(err);
            setMapError(true);
        } finally {
            setIsLoading(false)
        }
    };

    // X & Y Map coordinates
    useEffect(() => {
        if (mapInstance.current) {
            const coordDiv = document.querySelector("#coord_indctr_val")
            if (showMapCoords) {
                mapInstance.current.on("mousemove", (e: any) => {
                    const coords = mapCoords.project([e.latlng.lat, e.latlng.lng]);
                    if (coords.x > imageWidth) coords.x = imageWidth;
                    else if (coords.x < 0) coords.x = 0;
                    if (coords.y > imageHeight) coords.y = imageHeight;
                    if (coordDiv) coordDiv.textContent = `${coords.x.toFixed(0)} : ${coords.y.toFixed(0)}`;
                });
            } else mapInstance.current.off("mousemove")
        }
    }, [showMapCoords]);

    useEffect(() => {
        if (mapRef.current) {
            Array.from(mapRef.current.children).forEach((i: any) => {
                if ((i as HTMLElement).className !== "leaflet-control-container") {
                    (i as HTMLElement).style.filter = `brightness(${brightnessValue}%) contrast(${contrastValue}%)`;
                }
            });
        }
    }, [brightnessValue, contrastValue]);

    const undoRedoEdits = async () => {
        try {
            if (!localEditHistoryIds.length) return
            
            const currentIndex = Math.abs(localEditPointer)

            toggleGeomanButton(".icon-redo-edit", currentIndex === 0)
            toggleGeomanButton(".icon-undo-edit", currentIndex >= localEditHistoryIds.length - 1)
            
            const changeVerIdTo = localEditHistoryIds[currentIndex]
            setSelectedVersion(changeVerIdTo)
        } catch (err) {
            console.log(err)
        }
    }

    useEffect(() => {
        undoRedoEdits()
    }, [localEditPointer])    // eslint-disable-line

    const toggleGeomanButton = (selector: string, disable: boolean) => {
        const buttons = document.querySelectorAll(selector)
        
        buttons.forEach(btn => {
            const parent = btn.parentElement as HTMLElement
            if (!parent) return

            parent.style.boxShadow = "none"
            parent.style.pointerEvents = disable ? "none" : "auto"
            
            if (disable) parent.classList.add("pm-disabled")
            else parent.classList.remove("pm-disabled")
        })
    }

    useEffect(() => {
        localEditHistoryIdsRef.current = localEditHistoryIds
        if (localEditHistoryIds.length > 1) toggleGeomanButton(".icon-undo-edit", false)
        else {
            toggleGeomanButton(".icon-redo-edit", true)
            toggleGeomanButton(".icon-undo-edit", true)
        }
    }, [polyCreateTools, localEditHistoryIds])

    const undoAnnotationEdits = () => {
        console.log('undo')
        
        setLocalEditPointer(prevPointer => {
            if (prevPointer >= localEditHistoryIdsRef.current.length - 1) return prevPointer
            return prevPointer + 1
        })
    }
    const redoAnnotationEdits = () => {
        console.log('redo')

        setLocalEditPointer(prevPointer => {
            if (prevPointer <= 0) return prevPointer
            return prevPointer - 1
        })
    }

    const resetHighlights = () => {
        setMultiSelectList(prevState => {
            prevState.forEach(layer => {
                if (layer.properties.type === "pin") {
                    layer.setStyle({ fillColor: "blue", color: "blue" });
                } else if (layer.properties.type === 'polygon') {
                    layer.setStyle({ fillColor: layer.properties.color, color: layer.properties.color });
                } else if (layer.properties.type === 'line') {
                    layer.setStyle({ color: 'black' })
                }
            });
            return [];
        });
    };

    const selectMultiAnnotaions = (e: any) => {
        if (e) {
            multiSelect = !multiSelect;
            if (multiSelect) {
                freeDrawEnabled = true;
                enableFreeDraw()
                touchMapper.setDrawingMode(true)
            } else {
                disableFreeDraw();
                resetHighlights();
                touchMapper.setDrawingMode(false)
            }
        } else {
            disableFreeDraw()
            multiSelect = false;
            resetHighlights();
            touchMapper.setDrawingMode(false)
        }
    };

    const enableFreeDraw = () => {
        try {
            freeDrawRef.current.mode(FreeDraw.CREATE);
        } catch (err) {
            console.warn("Failed to enable FreeDraw: ", err);
        }
    }

    const disableFreeDraw = () => {
        freeDrawEnabled = false;

        if (!freeDrawRef.current) return;

        try {
            freeDrawRef.current.mode(FreeDraw.NONE);
            freeDrawRef.current.cancelAction?.();
            freeDrawRef.current.clear?.();
        } catch (err) {
            console.warn("Failed to reset FreeDraw: ", err);
        }
    };

    const initFreeDraw = () => {
        if (mapInstance.current) {
            const freeDraw = new FreeDraw();
            mapInstance.current.addLayer(freeDraw);
            freeDraw.mode(FreeDraw.NONE);
            return freeDraw;
        }
    };

    const selectDrawMarkers = (latLngs: any) => {
        try {
            const selecLayers: {[x: string]: any} = {};
            const selectionLayer = L.polygon(latLngs);
            const selectionBounds = selectionLayer.getBounds(); 
            const polygon = selectionLayer.toGeoJSON();

            mapInstance.current.eachLayer((layer: any) => {
                if (!(layer instanceof L.Path) || !layer.properties) return
                
                // If bounding boxes don't touch, skip
                if (layer.getBounds && !selectionBounds.intersects(layer.getBounds())) return

                const { type, id } = layer.properties

                const contain = (type === 'polygon'
                    ? turf.booleanOverlap(polygon, layer.toGeoJSON()) || turf.booleanWithin(layer.toGeoJSON(), polygon)
                    : type === 'pin'
                        ? turf.booleanPointInPolygon(layer.toGeoJSON(), polygon)
                        : type === 'line'
                            ? turf.booleanIntersects(layer.toGeoJSON(), polygon) || turf.booleanWithin(layer.toGeoJSON(), polygon)
                            : false
                )
                if (contain) {
                    selecLayers[id] = layer
                    layer.setStyle({ fillColor: "red", color: "red" })
                }
            })
            setMultiSelectList(prevState => {
                const selectedIds = new Set(Object.keys(selecLayers))
                const temp = prevState.filter(i => i?.properties?.id!==null && !selectedIds.has(String(i.properties.id)));
                return [...temp, ...Object.values(selecLayers)];
            });
        } catch (err) {
            console.log(err);
        }
    };

    const showLiveCoords = (event: any) => {
        const { workingLayer } = event;
        if (["ruler", "polygon"].includes(event.shape.toLowerCase())) {
            setShowLiveMeasurement(true);
            const tooltip = document.querySelector("#live-measurement");
            if (tooltip) tooltip.textContent = `0 mm`;
            let points: {x: number, y: number}[] = [];
            workingLayer.on("pm:vertexadded", (e: any) => (points = e.layer.getLatLngs().map((i: any) => mapCoords.project([i.lat, i.lng]))));
            mapInstance.current.on("mousemove", (e: any) => {
                if (points.length && tooltip) {
                    const vertices = mapCoords.project([e.latlng.lat, e.latlng.lng]);
                    const p = points[points.length - 1]
                    const d = (Math.sqrt((p.x - vertices.x) * (p.x - vertices.x) + (p.y - vertices.y) * (p.y - vertices.y)) / 4000).toFixed(3);
                    tooltip.textContent = `${d} mm`;
                }
            });
        } else if (event.shape.toLowerCase() === "rectangle") {
            setShowLiveMeasurement(true);
            const tooltip = document.querySelector("#live-measurement");
            if (tooltip) tooltip.innerHTML = `W: 0 mm<br/>H: 0 mm`;
            mapInstance.current.on("mousemove", (e: any) => {
                if (tooltip && workingLayer.getLatLngs()[0].length === 4) {
                    const rectangleCoords = workingLayer.getLatLngs()[0].map((i: any) => mapCoords.project([i.lat, i.lng]));
                    const width = (Math.sqrt(
                        (rectangleCoords[0].x - rectangleCoords[1].x) *
                        (rectangleCoords[0].x - rectangleCoords[1].x) +
                        (rectangleCoords[0].y - rectangleCoords[1].y) *
                        (rectangleCoords[0].y - rectangleCoords[1].y)
                    ) / 4000).toFixed(3);
                    const height = (Math.sqrt(
                        (rectangleCoords[0].x - rectangleCoords[3].x) *
                        (rectangleCoords[0].x - rectangleCoords[3].x) +
                        (rectangleCoords[0].y - rectangleCoords[3].y) *
                        (rectangleCoords[0].y - rectangleCoords[3].y)
                    ) / 4000).toFixed(3);
                    tooltip.innerHTML = `W: ${width} mm<br/>H: ${height} mm`;
                }
            });
        }
    };

    const toggleMarkerDrag = () => {
        dragMarkerEnabled = !dragMarkerEnabled;
        if (dragMarkerEnabled) touchMapper.setDrawingMode(true)
        else touchMapper.setDrawingMode(false)
    }

    const toggleCustomMarker = () => (drawPin = !drawPin);

    const initPolyCreationTools = () => {
        if (!mapInstance.current || !imageData || readonly || polyCreateTools) return

        try {
            freeDrawRef.current = initFreeDraw();

            const toggleFreeDraw = () => {
                freeDrawEnabled = !freeDrawEnabled;
                
                if (freeDrawEnabled) {
                    enableFreeDraw()
                    touchMapper.setDrawingMode(true)
                } else {
                    disableFreeDraw()
                    touchMapper.setDrawingMode(false)
                }
            };

            // Freedraw tool
            mapInstance.current.pm.Toolbar.createCustomControl({
                name: "freeDraw",
                title: "Free Draw",
                onClick: () => toggleFreeDraw(),
                className: "icon-freedraw",
            });
            // Ruler tool
            mapInstance.current.pm.Toolbar.copyDrawControl("Polyline", {
                name: "ruler",
                title: "Ruler",
                className: "icon-ruler",
            });
            // Custom Marker tool
            mapInstance.current.pm.Toolbar.createCustomControl({
                name: "pin",
                title: "Pin",
                className: "icon-pin-toggle",
                onClick: (e: any) => toggleCustomMarker(),
            });
            // Drag toggle tool
            mapInstance.current.pm.Toolbar.createCustomControl({
                name: "drag-toggle",
                title: "Toggle Polygon Dragging",
                className: "icon-drag-toggle",
                onClick: (e: any) => toggleMarkerDrag(),
            });
            // Multi select tool
            mapInstance.current.pm.Toolbar.createCustomControl({
                name: "select",
                title: "Select",
                className: "icon-select-multiple",
                onClick: (e: any) => selectMultiAnnotaions(e),
            });
            // Undo edits tool
            mapInstance.current.pm.Toolbar.createCustomControl({
                name: "undo",
                title: "Undo",
                className: "icon-undo-edit",
                toggle: false,
                onClick: () => undoAnnotationEdits(),
            });
            // Redo edits tool
            mapInstance.current.pm.Toolbar.createCustomControl({
                name: "redo",
                title: "Redo",
                className: "icon-redo-edit",
                toggle: false,
                onClick: () => redoAnnotationEdits(),
            });

            // Draw events
            freeDrawRef.current.on("markers", (e: any) => {
                if (e.eventType === "create" && e.latLngs.length > 0) {
                    try {
                        if (multiSelect) selectDrawMarkers(e.latLngs);
                        else {
                            const poly = L.polygon(e.latLngs).addTo(mapInstance.current);
                            const coordinates = poly.getLatLngs()[0].map((item: any) => {
                                const data = mapCoords.project(item);
                                return { X: data.x, Y: data.y };
                            });
                            document.querySelector<HTMLElement>(".icon-freedraw")?.click()
                            createPolyHandler(coordinates, poly, "poly", "Unknown");
                        }
                        freeDrawRef.current.clear()
                    } catch (err) {
                        console.log("freeDrawRef.on:markers: ", err)
                    }
                }
            });

            mapInstance.current.on("pm:drawstart", (event: any) => {
                try  {
                    showLiveCoords(event);
                    const { workingLayer } = event;
                    workingLayer.on("pm:vertexadded", (e: any) => {
                        const vertices = e.layer.getLatLngs();
                        if (e.shape === "ruler" && vertices.length >= 2) {
                            const btn = document.getElementsByClassName("leaflet-pm-action action-finish")
                            if (btn.length === 2) (btn[1] as HTMLElement).click();
                        }
                    });
                } catch (err) {
                    console.log("pm:drawstart: ", err)
                }
            });
            mapInstance.current.on("pm:create", (e: any) => {
                freeDrawRef.current.clear()
                disableFreeDraw()
                try  {
                    if (e.layer.pm._shape === "marker") return;
                    else if (e.layer.pm._shape === "ruler") {
                        const points = e.layer.getLatLngs();
                        const coordinates = points.map((item: any) => {
                            const data = mapCoords.project(item);
                            return { X: data.x, Y: data.y };
                        });
                        createPolyHandler(coordinates, e.layer, "line", "Unknown");
                    } else {
                        const points = e.layer.getLatLngs();
                        const coordinates = points[0].map((item: any) => {
                            const data = mapCoords.project(item);
                            return { X: data.x, Y: data.y };
                        });
                        createPolyHandler(coordinates, e.layer, "poly", "Unknown");
                    }
                } catch (err) {
                    console.log(err)
                }
            });
            mapInstance.current.on("pm:drawend", (e: any) => {
                setShowLiveMeasurement(false);
                mapInstance.current.off("mousemove");
            });
            mapInstance.current.on("click", (e: any) => {
                try {
                    if (!drawPin) return
                    const point = mapCoords.project(e.latlng);
                    createPolyHandler({ X: point.x, Y: point.y }, null, "pin", "Pin");
                } catch (err) {
                    console.log('click: ', err)
                }
            });
            // Map context menu
            mapInstance.current.on('dblclick', (e: any) => {
                setShowClassMenu(false)
                try {
                    // position for rendering context menu
                    const point = mapCoords.project(e.latlng)
                    setSelectedPoint(e.latlng)
                    if (point.x <= imageWidth && point.x >= 0 && point.y <= imageHeight && point.y >= 0) {
                        setContextMenuPos(prevState => prevState === null ?
                            { mouseX: e.originalEvent.clientX + 2, mouseY: e.originalEvent.clientY - 6 }
                            : null
                        )
                    }
                } catch (err) {
                    console.log("dblclick: ", err)
                }
            })
            mapInstance.current.on("contextmenu", (e: any) => {
                setShowClassMenu(false);
                try {
                    // position for rendering context menu
                    const point = mapCoords.project(e.latlng);
                    setSelectedPoint(e.latlng);
                    if (point.x <= imageWidth && point.x >= 0 && point.y <= imageHeight && point.y >= 0) {
                        setContextMenuPos((prevState) => prevState === null
                            ? {mouseX: e.originalEvent.clientX + 2, mouseY: e.originalEvent.clientY - 6}
                            : null
                        );
                    }
                } catch (err) {
                    console.log("contextmenu: ", err)
                }
            })

            setPolyCreateTools(true)
        } catch (err) {
            console.error("Poly tools init: ", err);
        }
    };

    const createWebGLPolygon = (geojson: GeoJSON, type: string) => {
        try {
            webGLPolygons = [];
            const chunkSize = 100000;
            const size = geojson.features.length / chunkSize;
            new Promise((resolve) => {
                let index = 0;
                const processChunk = () => {
                    const temp =
                        size > 1
                            ? Array.from({ length: Math.ceil(size) }, (v, i) => geojson.features.slice(i * chunkSize, i * chunkSize + chunkSize))
                            : [geojson.features];
                    temp.forEach((i) => {
                        if (type === "polygon") {
                            webGLPolygons.push(
                                L.glify.shapes({
                                    map: mapInstance.current,
                                    data: { type: "FeatureCollection", features: i },
                                    opacity: 0.6,
                                    border: true,
                                    color: (e: any, feature: any) => feature.properties.color,
                                })
                            );
                        } else {
                            webGLPolygons.push(
                                L.glify.points({
                                    map: mapInstance.current,
                                    data: { type: "FeatureCollection", features: i },
                                    opacity: 0.5,
                                    size: 15,
                                    color: (e: any, feature: any) => feature.properties.color,
                                })
                            );
                        }
                    });
                    index += chunkSize;
                    if (index < geojson.features.length) setTimeout(processChunk, 250);
                    else resolve("success");
                };
                processChunk();
            });
        } catch (err) {
            console.log(err);
        }
    };

    const clearMapLayers = useCallback(() => {  // To-Do: make variables useRef
        if (!mapInstance.current) return

        try {
            disableFreeDraw()

            // clear overlays
            if (heatMapOverlay) {
                mapInstance.current.removeLayer(heatMapOverlay);
                heatMapOverlay = null;
            }
            if (imageOverlay) mapInstance.current.removeLayer(imageOverlay);
            if (tileOverlayLayer.current) mapInstance.current.removeLayer(tileOverlayLayer.current);

            // clear webgl layer
            if (webGLPolygons.length) {
                for (let i of webGLPolygons) i.remove();
                webGLPolygons = [];
            }

            // clear other map layers e.g: geojson, polygons, ...
            mapInstance.current.eachLayer((layer: any) => {
                if (layer instanceof L.Path && "properties" in layer) mapInstance.current.removeLayer(layer);
                else if (layer instanceof L.GeoJSON) mapInstance.current.removeLayer(layer);
            });
        } catch (err) {
            console.log(err);
        }
    }, [])

    const renderUserEdits = () => {
        try {
            modifiedGsAnnotations.forEach(i => {
                if(renderAnnoType!.includes("user-cancer")) {
                    if (Array.isArray(i.coordinates))
                        if (i.category && i.category.toLowerCase()==="cancer")
                            createPolygon(i, true, "polygon")
                } else {
                    if (Array.isArray(i.coordinates)) {
                        if (i.category) createPolygon(i, true, "polygon")
                        else if ('distance' in i) createLines(i)
                    } else createCircleMarker(i)
                }
            })
        } catch (err) {
            console.log(err)
        }
    }

    const fetchOverlayTileLayer = (filename: string) => {
        if (!tileOverlayLayer.current) return

        const key = filename.split("/").slice(1).join("/")

        tileOverlayLayer.current.createTile = (coords: any, done: Function) => {
            const payload = {
                bucket: filename.split("/")[0],
                key: key,
                max_zoom: Number(imageZoomLvl),
                z: Number(coords.z),
                x: Number(coords.x),
                y: Number(coords.y)
            };
            const tile = document.createElement('img')
            customPost(`${tileAppUrl}/tile/overlay-tile`, payload, {
                responseType: 'blob',
                headers: {
                    "Content-Type": "application/json",
                    'Accept': 'image/avif,image/webp,image/apng,image/svg+xml,image/*,*/*;q=0.8',
                    "X-API-Key": tileApiKey
                }
            })
                .then(blob => {
                    if (!(blob instanceof Blob)) throw new Error("Response is not a blob");
                    const url = URL.createObjectURL(blob);
                    tile.onload = () => {
                        tile.style.visibility = 'visible';
                        URL.revokeObjectURL(url);   // Free up memory after the image is rendered
                        done(null, tile);
                    };
                    tile.onerror = () => {
                        URL.revokeObjectURL(url);
                        done("Tile load error", tile);
                    }
                    tile.src = url;
                })
                .catch((err) => {
                    console.error("Error loading tile:", err);
                    done(err, tile)
                });
            return tile;
        };
        mapInstance.current.addLayer(tileOverlayLayer.current);
        setIsMarkersLoading(false);
    };

    const jsonTileOverlay = async (filename: string, reload: boolean) => {
        setIsMarkersLoading(true)
        try {
            const key = filename.split("/").slice(1).join("/")
            const payload = {
                bucket: filename.split("/")[0],
                key: key,
                resolution: [imageWidth, imageHeight],
                reset_image: reload
            }
            const res = await customPost(`${tileAppUrl}/tile/overlay`, payload, {
                headers: {
                    "Content-Type": "application/json",
                    "X-API-Key": tileApiKey
                }
            })
            if (res.status === "Done") fetchOverlayTileLayer(filename)
            else setTileMsg({message: res.status, type: "success"})
        } catch (err) {
            console.log(err)
            setTileMsg({ message: 'Unknown error', type: "error" })
        } finally {
            setIsMarkersLoading(false)
            setJsonClasses(prevState => ({...prevState, [filename]: []}))
        }
    }

    const toggleLoading = (type: string, value: any) => {
        if (type === 'bcr-heatmap') setBcrHeatmapLoading(value)
        else if (type === 'xml-load') setToast(value)
    }

    const renderGeojsonPolygon = (geojsonData: GeoJSON, filter: string[], filterReverse: boolean = false) => {
        try {
            const fixedFilter = filter.map((i) => i && i.toLowerCase()).filter((i) => i && i.length);
            return L.geoJSON(geojsonData, {
                style: (feature: any) => ({color: feature.properties.color || defaultMarkerColor}),
                filter: (feature: any) => fixedFilter.length
                    ? filterReverse
                        ? fixedFilter.includes(feature.properties.category.toLowerCase())
                        : !fixedFilter.includes(feature.properties.category.toLowerCase())
                    : true,
                onEachFeature: (feature: any, layer: any) => {
                    // const latlngs = layer.getLatLngs()
                    // let vertexCount = 0
                    // if (Array.isArray(latlngs[0])) vertexCount = latlngs[0].length
                    // else vertexCount = latlngs.length
                    // layer.bindTooltip(`${feature.properties.category}<br>${vertexCount}`, { className: 'marker-popup' })
                    layer.bindTooltip(`${feature.properties.category}`, {className: "marker-popup"});
                },
                pointToLayer: (feature: any, latlng: any) => L.circleMarker(latlng, {
                    radius: 7,
                    color: "blue",
                    fillOpacity: 0.2,
                }),
            }).addTo(mapInstance.current);
        } catch (err) {
            console.error(err);
        }
    };

    const mergedAnnotationList = useCallback(() => {
        try {
            return mergePolyList(sysGsAnnotations, modifiedGsAnnotations)
        } catch (err) {
            console.log(err)
            return []
        }
    }, [sysGsAnnotations, modifiedGsAnnotations])

    const renderAnnotations = () => {
        try {
            const markers = mergedAnnotationList()
            markers.forEach((i: any) => {
                if (Array.isArray(i.coordinates)) {
                    if (i.category) createPolygon(i, true, "polygon")
                    // else if ('distance' in i) createLines(i)
                } else createCircleMarker(i)
            })
        } catch (err) {
            console.log(err)
        }
    }

    useEffect(() => {
        clearMapLayers()
        if (!renderAnnoType) return

        if (renderAnnoType.includes("all")) renderAnnotations()
        else if (renderAnnoType.includes("user")) renderUserEdits()
        else if (renderAnnoType.includes("bcr-heatmap")) initHeatmap()

        // Reset XML and its class filters when opening other polygons
        if (!renderAnnoType.includes("xml")) {
            setXmlClassFilter({})
            setXmlNames([])
        }
        // clear JSON heatmaps
        setJsonName('')
        setJsonClassFilter('')
        // system markers checks
        setSysClassFilter([])
        // clear user checks
        setUserFilter([])
        // clear bcr heat types
        setBCRClassFilter([])
    }, [renderAnnoType])  // eslint-disable-line

    const showSysAnno = (e: React.ChangeEvent<HTMLInputElement>) => {
        handleAnnoCheckbox(e.target.id)
        // clear 'cancer' type check
        userAnnoRef.current = false
        if (e.target.checked) setRenderAnnoType(`all-${uuidv4()}`)
        else setRenderAnnoType(null)
    }

    useEffect(() => {
        if (sysClassFilter.length) {
            const markers = mergedAnnotationList()
            markers.forEach((i: any) => {
                if (Array.isArray(i.coordinates) && i.category) {
                    if (sysClassFilter.includes(i.category.toLowerCase())) createPolygon(i, true, "polygon")
                }
            })
        }
    }, [sysClassFilter])  // eslint-disable-line

    const showByCategory = useCallback((category: string) => {
        // clear polygons on map
        clearMapLayers()
        // clear xml checks
        setXmlClassFilter({})
        setXmlNames([])
        // clear json checks
        setJsonName('')
        setJsonClassFilter('')
        // clear user checks
        setUserFilter([])
        // clear bcr heat types
        setBCRClassFilter([])
        // clear 'cancer' type check
        userAnnoRef.current = false

        setSysClassFilter(prevList => {
            if (prevList.includes(category)) return prevList.filter(i => i!==category)
            else return [...prevList, category]
        })
    }, [clearMapLayers])

    const showUserAnno = (e: React.ChangeEvent<HTMLInputElement>) => {
        handleAnnoCheckbox(e.target.id)
        // clear 'cancer' type check
        userAnnoRef.current = false
        if (e.target.checked) setRenderAnnoType(`user-${uuidv4()}`)
        else setRenderAnnoType(null)
    }

    useEffect(() => {
        if (userFilter.length) {
            modifiedGsAnnotations.forEach(i => {
                if (userFilter.includes(i.modified_by?.id || 0)) {
                    if (Array.isArray(i.coordinates)) {
                        if (i.category) createPolygon(i, true, "polygon")
                        else if ('distance' in i) createLines(i)
                    } else createCircleMarker(i)
                }
            })
        }
    }, [userFilter])  // eslint-disable-line

    const showByUser = useCallback((userId: number) => {
        // clear polygons on map
        clearMapLayers()
        // clear xml checks
        setXmlClassFilter({})
        setXmlNames([])
        // clear json checks
        setJsonName('')
        setJsonClassFilter('')
        // clear system checks
        setSysClassFilter([])
        // clear bcr heat types
        setBCRClassFilter([])
        // clear 'cancer' type check
        userAnnoRef.current = false
        
        setUserFilter(prevList => {
            if (prevList.includes(userId)) return prevList.filter(i => i!==userId)
            else return [...prevList, userId]
        })
    }, [clearMapLayers])

    const userAnnotationCancer = (e: React.ChangeEvent<HTMLInputElement>) => {
        handleAnnoCheckbox(e.target.id)
        if (e.target.checked) {
            userAnnoRef.current = true
            setRenderAnnoType(`user-cancer-${uuidv4()}`)
        } else setRenderAnnoType(null)
    }

    const initHeatmap = () => {
        if (!mapInstance.current || !bcrAnnotations) return

        try {
            if (bcrHeatmap.current) heatMapOverlay = bcrHeatmap.current.addTo(mapInstance.current)
            else {
                const heatMapPoints = processBcrHeatData(bcrAnnotations, imageWidth, imageHeight);
                const tempHeatMap = drawHeatMap(
                    (document.getElementById("heatmap-container") as HTMLDivElement),
                    heatMapPoints,
                    mapCoords,
                    imageWidth,
                    imageHeight,
                    1
                )
                if (tempHeatMap) {
                    heatMapOverlay = tempHeatMap
                    bcrHeatmap.current = tempHeatMap
                    tempHeatMap.addTo(mapInstance.current)
                }
            }
        } catch (err) {
            console.log(err);
        }
    };

    const showHeatMap = (e: React.ChangeEvent<HTMLInputElement>) => {
        handleAnnoCheckbox(e.target.id)
        // clear 'cancer' type check
        userAnnoRef.current = false
        if (e.target.checked && bcrAnnotations) setRenderAnnoType(`bcr-heatmap-${uuidv4()}`)
        else setRenderAnnoType(null)
    }

    const getVersion = async () => {
        if (!imageData) return []
        // Slides in master-dataset directory (internal viewer) will show version from all users
        if (imageData.input_s3 && imageData.input_s3 === 'master-dataset') {
            const res = await get(`Annotations/versions-by-type/${imageData.id}`)
            if (!res.length) return []
            setVersions(res)
            setSelectedVersion(res[0].id)
            setLocalEditHistoryIds([res[0].id])
            return res
        } else {
            // Slides in user directory (My Folder/Mounts/Shared) will only show
            // versions from their own and the users the slide shared with
            const userIds: number[] = [...new Set([...imageData.uploaded_by, ...imageData.shared])]
            const res = await post(`Annotations/versions-by-type/${imageData.id}`, {user_ids: userIds})
            if (!res.length) return []
            setVersions(res)
            setSelectedVersion(res[0].id)
            setLocalEditHistoryIds([res[0].id])
            return res
        }
    }

    const updateCurrentEdits = async (newVerId: number, sourceVerId: number) => {
        try {
            setLocalEditHistoryIds(prevList => ([newVerId, ...prevList]))
            setVersions(prevList => {
                if (!prevList.length) {
                    return [{
                        id: newVerId,
                        image_id: imageData!.id,
                        user_id: userId,
                        adddate: new Date().toISOString().split("T")[0]
                    }]
                }
                return prevList.map(i => {
                    if (i.sys_marker_ver === sourceVerId) return {...i, id: newVerId}
                    return i
                })
            })
            setActiveVerId(newVerId)
        } catch (err) {
            console.log(err);
        }
    };

    const applyBulkAnnotationUpdates = (updatedMarkers: Annotation[]) => {
        setModifiedGsAnnotations(prev => {
            const next = new Map(prev.map(marker => [marker.id, marker]))
            for (const marker of updatedMarkers) {
                next.set(marker.id, marker)
            }
            return Array.from(next.values())
        })
    }

    const updateModifiedAnnoInList = (editedMarker: Annotation) => {
        setModifiedGsAnnotations(prevState => {
            const exist = prevState.findIndex(i => i.id === editedMarker.id)
            if (exist !== -1) {
                prevState[exist] = editedMarker
                return [...prevState]
            } else return [...prevState, editedMarker]
        })
    }

    const updateEditedMarkerOnSlide = (oldMarker: any, markerData: Annotation) => {
        try {
            mapInstance.current.removeLayer(oldMarker);
            if (markerData.category) createPolygon(markerData, true, "polygon");
        } catch (err) {
            console.log(err);
        }
    };

    const editPolygon = async (polyLayer: any) => {
        setIsMarkersLoading(true);
        try {
            console.log(polyLayer)
            const rings = polyLayer.getLatLngs()
            // Leaflet's getLatLngs() returns an array of rings for a single polygon.
            // (If the array is wrapped an extra time due to a MultiPolygon conversion glitch, unwrap it with a quick check)
            const normalizedRings = (rings.length === 1 && Array.isArray(rings[0][0]) && !('lat' in rings[0][0])) 
                ? rings[0] 
                : rings

            // Separate outer boundary and holes
            const outerBoundaryLatLngs = normalizedRings[0] || []
            const holesLatLngs = normalizedRings.slice(1) || []

            // Convert outer boundary back to your {X, Y} format
            const updatedCoordinates = outerBoundaryLatLngs.map((latlng: any) => {
                const point = mapCoords.project(latlng)
                return { X: point.x, Y: point.y }
            })
            // Convert holes back to an array of {X, Y} arrays
            const updatedHoles = holesLatLngs.map((holeRing: any) => {
                return holeRing.map((latlng: any) => {
                    const point = mapCoords.project(latlng)
                    return { X: point.x, Y: point.y }
                })
            })

            const payload = {
                prev_marker_data: {
                    id: polyLayer.properties.id,
                    category: polyLayer.properties.category,
                    coordinates: polyLayer.properties.coordinates,
                    holes: polyLayer.properties.holes,
                },
                category: polyLayer.properties.category,
                coordinates: updatedCoordinates,
                holes: updatedHoles,
                user_id: userId,
                image_id: imageData!.id,
                base_version: sysMarkerVerRef.current
            }

            const res = await put(`Annotations/image/${imageData!.id}`, payload)
            
            // update category list, prediction info, version info and modified annottaion list
            updateModifiedAnnoInList(res.marker)
            setGsPredictionResult(processPredictionResults(res.prediction_result, res.categories))
            updateCurrentEdits(res.id, res.sys_marker_ver)
            // update edited marker on the slide visually
            updateEditedMarkerOnSlide(polyLayer, res.marker)
        } catch (err) {
            console.log(err)
        } finally {
            setIsMarkersLoading(false)
            editLayer = {}
        }
    }

    const editPolygonHandler = (layer: any) => {
        if (layer.getLatLngs()) editPolygon(layer);
        else console.info("No layer data");
    };

    const updateDeletedAnnoInList = (deletedMarker: Annotation) => {
        setModifiedGsAnnotations(prevState => {
            if (!prevState.length) return prevState

            const exist = prevState.findIndex(i => i.id===deletedMarker.id)
            if (exist!==-1) {
                prevState[exist].is_deleted = true
                return [...prevState]
            } else return [...prevState, {...deletedMarker, is_deleted: true}]
        })
    }

    const removeMarker = async () => {
        setWaitingForRes(true);
        try {
            const payload = {
                marker: selectedMarker.properties,
                user_id: userId,
                image_id: imageData!.id,
                base_version: sysMarkerVerRef.current
            };
            const res = await post(`Annotations/image/delete/${imageData!.id}`, payload);
            // update category list, prediction info, version info and modified annotation list
            if (!["line", "pin"].includes(selectedMarker.properties.type)) {
                setGsPredictionResult(processPredictionResults(res.prediction_result, res.categories));
            }
            updateDeletedAnnoInList(selectedMarker.properties);
            updateCurrentEdits(res.id, res.sys_marker_ver);
            // remove marker on the slide visually
            mapInstance.current.removeLayer(selectedMarker);
        } catch (err) {
            console.log(err);
        } finally {
            setWaitingForRes(false);
            closeDeleteModal();
            editLayer = {};
        }
    };

    const removeMarkerList = async () => {
        setWaitingForRes(true);
        try {
            const payload = {
                marker_list: multiSelectList.map((i) => i.properties),
                image_id: imageData!.id,
                user_id: userId,
                base_version: sysMarkerVerRef.current
            }
            const res = await post(`Annotations/image/delete-list/${imageData!.id}`, payload)

            setModifiedGsAnnotations(prev => {
                const nextMap = new Map()

                for (const annotation of prev) {
                    nextMap.set(String(annotation.id), annotation)
                }

                for (const layer of multiSelectList) {
                    const id = String(layer.properties.id)
                    const existingAnnotation = nextMap.get(id)

                    if (existingAnnotation) nextMap.set(id, { ...existingAnnotation, is_deleted: true })
                    else nextMap.set(id, { ...layer.properties, is_deleted: true })
                }

                return Array.from(nextMap.values())
            })
            for (const layer of multiSelectList) {
                mapInstance.current.removeLayer(layer)
            }

            // // update category list, prediction info, version info and modified annotation list
            // multiSelectList.forEach((i) => {
            //     updateDeletedAnnoInList(i.properties)
            //     mapInstance.current.removeLayer(i)
            // })

            setGsPredictionResult(processPredictionResults(res.prediction_result, res.categories));
            updateCurrentEdits(res.id, res.sys_marker_ver);
        } catch (err) {
            console.log(err);
        } finally {
            setWaitingForRes(false);
            closeDeleteModal();
            editLayer = {};
            setMultiSelectList([])
        }
    };

    const changeCategory = async (category: ColorLibObj) => {
        setIsMarkersLoading(true);
        closeMapContextMenu();
        if (multiSelect) {
            try {
                if (multiSelectList.length > MAX_BULK_SELECTION) {
                    setServerError(`Select a smaller area with less than ${MAX_BULK_SELECTION} annotations.`)
                    return
                }
                const selectedLayers = multiSelectList.filter(layer => layer?.properties?.category)
                const selectedById = new Map(selectedLayers.map(layer => [String(layer.properties.id), layer]))

                const payload = {
                    marker_list: multiSelectList.filter(i => i.properties.category).map(i => ({
                        id: i.properties.id,
                        category: i.properties.category,
                        coordinates: i.properties.coordinates,
                        holes: i.properties.holes
                    })),
                    category: category.name,
                    image_id: imageData!.id,
                    user_id: userId,
                    base_version: sysMarkerVerRef.current
                };
                const res = await put(`Annotations/image/edit-list`, payload)

                applyBulkAnnotationUpdates(res.marker_list)

                for (const marker of res.marker_list) {
                    const layer = selectedById.get(String(marker.id))

                    if (!layer) continue

                    const color = colorLib.find(x => x.name.toLowerCase() === marker.category?.toLowerCase())?.color ?? defaultMarkerColor
                    let area = null
                    if (layer.properties.area && !isNaN(layer.properties.area)) {
                        const rawArea = layer.properties.area / 16000000
                        area = `${rawArea !== 0 && rawArea < 0.01
                            ? rawArea.toFixed(1 - Math.floor(Math.log(rawArea) / Math.log(10)))
                            : rawArea.toFixed(2)
                        } mm²`
                    }
                    const tooltipText = area
                        ? `${category.name.length <= 4 ? category.name.toUpperCase() : category.name} <wbr>Area: ${area}`
                        : category.name

                    layer.properties.color = color
                    layer.properties.category = marker.category
                    layer.properties.coordinates = marker.coordinates
                    layer.setTooltipContent(tooltipText)
                    layer.setStyle({ color: color, fillColor: color })
                }

                setGsPredictionResult(processPredictionResults(res.prediction_result, res.categories));
                updateCurrentEdits(res.id, res.sys_marker_ver);

                if (serverError.length) setServerError('');
            } catch (err) {
                console.log(err)
                setServerError('!')
            } finally {
                document.querySelector<HTMLElement>(".icon-select-multiple")?.click() // disable multi-select tool
                setIsMarkersLoading(false);
                setMultiSelectList([]);
                editLayer = {};
            }
        } else {
            try {
                const payload: any = {
                    prev_marker_data: {},
                    category: category.name,
                    coordinates: [],
                    image_id: imageData!.id,
                    user_id: userId,
                    base_version: sysMarkerVerRef.current
                }

                let polyIndex = modifiedGsAnnotations.findIndex(i => i.id === selectedMarker.properties.id);
                if (polyIndex !== -1) {
                    if (!modifiedGsAnnotations[polyIndex].category) throw Error("selected marker lack 'category'")
                    payload.prev_marker_data = {
                        id: modifiedGsAnnotations[polyIndex].id,
                        category: modifiedGsAnnotations[polyIndex].category,
                        coordinates: modifiedGsAnnotations[polyIndex].coordinates,
                        holes: modifiedGsAnnotations[polyIndex].holes
                    }
                    payload.coordinates = modifiedGsAnnotations[polyIndex].coordinates;
                    payload.holes = modifiedGsAnnotations[polyIndex].holes
                } else {
                    polyIndex = sysGsAnnotations.findIndex((i) => i.id === selectedMarker.properties.id);
                    if (polyIndex === -1) throw Error("selected marker not found")
                    payload.prev_marker_data = {
                        id: sysGsAnnotations[polyIndex].id,
                        category: sysGsAnnotations[polyIndex].category,
                        coordinates: sysGsAnnotations[polyIndex].coordinates,
                        holes: sysGsAnnotations[polyIndex].holes
                    }
                    payload.coordinates = sysGsAnnotations[polyIndex].coordinates;
                    payload.holes = sysGsAnnotations[polyIndex].holes
                }

                // Update the edit if it's active
                if (selectedMarker && editTool) {
                    selectedMarker.pm.toggleEdit({ allowSelfIntersection: false })

                    const rings = selectedMarker.getLatLngs()
                    // Leaflet's getLatLngs() returns an array of rings for a single polygon.
                    // (If the array is wrapped an extra time due to a MultiPolygon conversion glitch, unwrap it with a quick check)
                    const normalizedRings = (rings.length === 1 && Array.isArray(rings[0][0]) && !('lat' in rings[0][0])) 
                        ? rings[0] 
                        : rings

                    // Separate outer boundary and holes
                    const outerBoundaryLatLngs = normalizedRings[0] || []
                    const holesLatLngs = normalizedRings.slice(1) || []

                    // Convert outer boundary back to your {X, Y} format
                    const updatedCoordinates = outerBoundaryLatLngs.map((latlng: any) => {
                        const point = mapCoords.project(latlng)
                        return { X: point.x, Y: point.y }
                    })
                    // Convert holes back to an array of {X, Y} arrays
                    const updatedHoles = holesLatLngs.map((holeRing: any) => {
                        return holeRing.map((latlng: any) => {
                            const point = mapCoords.project(latlng)
                            return { X: point.x, Y: point.y }
                        })
                    })

                    payload.coordinates = updatedCoordinates
                    payload.holes = updatedHoles
                }

                const res = await put(`Annotations/image/${imageData!.id}`, payload);
                // update category list, prediction info, version info and modified annotation list
                updateModifiedAnnoInList(res.marker);
                setGsPredictionResult(processPredictionResults(res.prediction_result, res.categories));
                updateCurrentEdits(res.id, res.sys_marker_ver);
                // update edited marker on the slide visually
                updateEditedMarkerOnSlide(selectedMarker, res.marker);
                if (serverError.length) setServerError('');
            } catch (err) {
                console.log(err)
                setServerError('.');
            } finally {
                setSelectedMarker(null);
                setIsMarkersLoading(false);
                editLayer = {};
            }
        }
    };

    const zoomToPolygon = (markerId: string, hide: boolean) => {
        try {
            if (hide) {
                mapInstance.current.eachLayer((layer: any) => {
                    if ("properties" in layer && layer.properties.id === markerId) {
                        const colorIndex: number = colorLib.findIndex(i => i.name.toLowerCase() === layer.properties.category.toLowerCase())
                        const color: string = colorIndex !== -1 ? colorLib[colorIndex].color : defaultMarkerColor
                        layer.setStyle({color: color, fillColor: color})
                        return
                    }
                })
            } else {
                let points = []
                let markerIndex = modifiedGsAnnotations.findIndex(i => i.id===markerId)
                if (markerIndex===-1) {
                    markerIndex = sysGsAnnotations.findIndex(i => i.id===markerId)
                    if (markerIndex===-1) throw Error('Marker not found')
                    else {
                        points = sysGsAnnotations[markerIndex].coordinates.map((vert: any) => mapCoords.unproject([vert.X, vert.Y]))
                    }
                } else {
                    const temp = modifiedGsAnnotations[markerIndex].coordinates
                    if (Array.isArray(temp)) points = temp.map((vert: any) => mapCoords.unproject([vert.X, vert.Y]))
                }
            
                const bounds = L.latLngBounds()
                points.forEach((point: any) => bounds.extend([point.lat, point.lng]))
                const northEast = bounds.getNorthEast()
                mapInstance.current.setView(northEast, 5)
                mapInstance.current.fitBounds(bounds)
                mapInstance.current.eachLayer((layer: any) => {
                    if ("properties" in layer && layer.properties.id === markerId) {
                        layer.setStyle({color: "red", fillColor: "red"})
                        return
                    }
                })
            }
        } catch (err) {
            console.log(err)
        }
    }

    const categoryListItems = () => {
        return colorLib.map((x, id) => (
            <MenuItem key={id} onClick={() => changeCategory(x)}>
                <FontAwesomeIcon icon={faSquare} color={x.color} />&nbsp;{x.name}
            </MenuItem>
        ))
    };

    const getImageData = async () => {
        setIsLoading(true);
        try {
            await updateAnnoClassLibrary();
            const res = await get(`Images/string-id/${imageProp.file_id}`);
            // Check whether the XML file exists if the predictions fail
            if (
                res.tissuearea_status.toLowerCase() === "failed" ||
                res.bcr_status.toLowerCase() === "failed" ||
                res.annotation_status.toLowerCase() === "failed"
            ) {
                const predStatus = await get(`S3/pred-file-status/${imageProp.file_id}`);
                const payload: any = {};
                if (predStatus.tissuearea_status) payload.tissuearea_status = "Processed";
                if (predStatus.annotation_status) payload.annotation_status = "Processed";
                if (predStatus.bcr_status) payload.bcr_status = "Processed";
                const img = await put(`Images/${res.id}`, payload);
                setImageData(img);
            } else setImageData(res);
            if (res.bcr_score) updateBCRScore(Number(res.bcr_score.toFixed(2)))
            setImageError(false)
        } catch (err) {
            console.log(err);
            setImageError(true)
        } finally {
            setVersions([]);
        }
    };

    useEffect(() => {
        return () => cleanUp();
    }, []); // eslint-disable-line

    useEffect(() => {
        if (imageProp) getImageData();
        return () => cleanUp();
    }, [imageProp]); // eslint-disable-line

    const loadAnnotations = async () => {
        if (!selectedVersion) return

        setActiveVerId(selectedVersion)
        setIsMarkersLoading(true)
        setUserAnnoLoader(true)
        try {
            const prevSysMarkerVer = sysMarkerVerRef.current
            // update categories, prediction info and modified anno with the version
            const res = await getModifiedGSData(selectedVersion);
            setSysMarkerVer(res.sys_marker_ver);
            setModifiedGsAnnotations(res.custom_markers);
            setGsPredictionResult(processPredictionResults(res.prediction_result, res.categories));
            // sysMarkerVer is not changed, Avoid 2 render and showing incorrect polygons
            if (res.sys_marker_ver === prevSysMarkerVer) setRenderAnnoType(`all-${uuidv4()}`)
        } catch (err) {
            console.log(err)
        } finally {
            setUserAnnoLoader(false)
            setIsMarkersLoading(false);
            editLayer = {}
        }
    };

    useEffect(() => {
        loadAnnotations()
    }, [selectedVersion])    // eslint-disable-line

    const handleVersionChange = async (data: Version) => {
        setLocalEditHistoryIds([])
        setSelectedVersion(data.id)
    }

    const renderXMLAnnotations = () => {
        try {
            if (!xmlNames.length) return
            
            // clear polygons on map
            clearMapLayers()
            // clear json checks
            setJsonName('')
            setJsonClassFilter('')
            // clear system checks
            setSysClassFilter([])
            // clear user checks
            setUserFilter([])
            // clear bcr heat types
            setBCRClassFilter([])
            // clear 'cancer' type check
            userAnnoRef.current = false

            xmlNames.forEach(name => {
                if (name.length && name in xmlPolygonData) {
                    const filter = name in xmlClassFilter ? xmlClassFilter[name] : []
                    renderGeojsonPolygon(xmlPolygonData[name], filter)
                }
            })
        } catch (err) {
            console.log(err)
        }
    }

    const fetchXMLData = async (signal = null) => {
        setIsMarkersLoading(true);
        try {
            const promises = xmlNames.map(async (name) => {
                if (name.length && !(name in xmlPolygonData) && !ongoingXmlReq.current.has(name)) {
                    ongoingXmlReq.current.add(name);
                    try {
                        const { xml, size } = await getXmlFile(name.split("/").slice(1).join("/"), name.split("/")[0], signal); // eslint-disable-line
                        const { geoJsonData, categories } = processXMLMarkers(xml, false, mapCoords, colorLib);
                        xmlPolygonData = {...xmlPolygonData, [name]: geoJsonData};
                        setXmlClasses((prev) => ({ ...prev, [name]: categories }));
                    } catch (err) {
                        console.log(err);
                        setXmlClasses((prev) => ({ ...prev, [name]: [] }));
                    } finally {
                        ongoingXmlReq.current.delete(name);
                    }
                }
            });
            await Promise.all(promises).then(() => renderXMLAnnotations())
        } catch (err) {
            console.log(err);
        } finally {
            setIsMarkersLoading(false);
        }
    };

    useEffect(() => {
        if (xmlNames.length) fetchXMLData()
    }, [xmlNames])  // eslint-disable-line

    useEffect(() => {
        if (Object.keys(xmlClassFilter).length) renderXMLAnnotations()
    }, [xmlClassFilter])  // eslint-disable-line

    const showJsonTileOverlay = (render: boolean, name: string, reset: boolean = false) => {
        try {
            // clear polygons on map
            clearMapLayers()
            // clear xml checks
            setXmlClassFilter({})
            setXmlNames([])
            // clear system checks
            setSysClassFilter([])
            // clear user checks
            setUserFilter([])
            // clear bcr heat types
            setBCRClassFilter([])
            // clear 'cancer' type check
            userAnnoRef.current = false

            if (render) {
                setJsonName(name)
                jsonTileOverlay(name, reset)
            } else {
                setJsonName('')
                handleCloseTileMsg()
            }
        } catch (err) {
            console.log(err)
        }
    }

    const getSysMarkerData = async (signal: AbortSignal) => {
        setIsMarkersLoading(true);
        setSysAnnoLoader(true)
        try {
            const res = await getSysAnnoData(sysMarkerVer, signal);
            setSysGsAnnotations(res.markers);
        } catch (err) {
            setSysGsAnnotations([]);
        } finally {
            setSysAnnoLoader(false)
            setIsMarkersLoading(false);
            setRenderAnnoType(`all-${uuidv4()}`);
        }
    };

    useEffect(() => {
        sysMarkerVerRef.current = sysMarkerVer
        const reqAbortCont = new AbortController()
        getSysMarkerData(reqAbortCont.signal)
        return () => reqAbortCont.abort("Stoped sys marker stream")
    }, [sysMarkerVer])  // eslint-disable-line

    useEffect(() => {
        if (imageData) initMap();
    }, [imageData]) // eslint-disable-line

    const contextMenuDeleteHandler = () => {
        closeMapContextMenu();
        setDeleteModal(true);
    };

    const addNewHandler = () => {
        setContextMenuPos(null);
        setAddNewTag((prevState) => !prevState);
    };

    const updatePreditionResult = async () => {
        try {
            const payload = {
                image_id: imageData!.id,
                user_id: userId,
                file_id: imageData!.image_unique_id,
                base_version: sysMarkerVerRef.current
            };
            const res = await post("Annotations/recalc-prediction-result", payload);
            setGsPredictionResult(processPredictionResults(res.prediction_result, res.categories));
        } catch (err) {
            console.log(err);
        }
    };

    useEffect(() => {
        if (!bcrClassFilter.length || !bcrAnnotations) return
        renderGeojsonPolygon(bcrAnnotations, bcrClassFilter, true)
    }, [bcrClassFilter])  // eslint-disable-line

    const showHeatMapByClass = useCallback((heatType: string) => {  
        try {
            handleAnnoCheckbox('')
            // clear polygons on map
            clearMapLayers()
            // clear xml checks
            setXmlClassFilter({})
            setXmlNames([])
            // clear json checks
            setJsonName('')
            setJsonClassFilter('')
            // clear system checks
            setSysClassFilter([])
            // clear user checks
            setUserFilter([])
            // clear 'cancer' type check
            userAnnoRef.current = false

            setBCRClassFilter(prevList => {
                if (prevList.includes(heatType)) return prevList.filter(i => i!==heatType)
                else return [...prevList, heatType]
            })
        } catch (err) {
            console.log(err)
        }
    }, [clearMapLayers])

    const processBCRHeatmap = (xmlDoc: any) => {
        let catgList: {[key: string]: any} = {}
        try {
            if (!xmlDoc) throw Error("no data")

            const { geoJsonData, categories } = processXMLMarkers(xmlDoc, false, mapCoords, colorLib);
            catgList = categories
            setBcrAnnotations(geoJsonData)
            if ((document.getElementById("check-user-bcr") as HTMLInputElement).checked) setRenderAnnoType(`bcr-heatmap-${uuidv4()}`)
        } catch (err) {
            console.log(err);
        } finally {
            return catgList
        }
    };

    const updateBCRScore = (score: number) => setBCRScore(score);

    const getNumber = (value: number | number[]): number => {
        if (Array.isArray(value)) return value[0]
        return value
    }
    const adjustBrightness = (event: Event, value: number | number[], activeThumb: number) => setBrightnessValue(getNumber(value));
    const adjustContrast = (event: Event, value: number | number[], activeThumb: number) => setContrastValue(getNumber(value));

    const openDrawer = () => setDrawerOpen(true);
    const closeDrawer = () => setDrawerOpen(false);

    const handleCloseTileMsg = () => setTileMsg(null);

    const locateTile = () => {
        setTileInputCoord({ x: null, y: null });
        setTileInput((prevState) => !prevState);
    };
    const handleTileSearch = (e: React.ChangeEvent<HTMLInputElement>) => setTileInputCoord((prevState) => ({...prevState, [e.target.name]: Number(e.target.value)}));
    const point2Tile = (vertex: any) => {
        try {
            const location = mapCoords.unproject([vertex[0], vertex[1]]);
            mapInstance.current.setView(location, imageZoomLvl);
        } catch (err) {
            console.log(err);
        }
    };
    const searchTileLoc = () => point2Tile([tileInputCoord.x, tileInputCoord.y]);

    const getGenomeData = async (key: string, grid_key: string, listId: string) => {
        try {
            if (!genomicData[listId]) {
                const gene_expression = await post("S3/loadfile-pytorch", {
                    s3_name: key.split("/")[0],
                    gene_key: key.split("/").slice(1).join("/"),
                    grid_key: grid_key,
                });
                genomicData[listId] = gene_expression;
            }
            return genomicData[listId].gene_name;
        } catch (err) {
            console.log(err);
        }
    };

    const showGenomeHeatMap = (e: React.ChangeEvent<HTMLInputElement>, geneListId: string, geneName: string) => {
        try {
            handleAnnoCheckbox(e.target.id);
            // clear 'cancer' type check
            userAnnoRef.current = false;
            clearMapLayers();
            if (!e.target.checked) return;

            const { heatValues, max } = processGeneHeatData(
                genomicData[geneListId],
                geneName,
                imageWidth,
                imageHeight
            );
            const tempHeatMap = drawHeatMap(
                (document.getElementById("heatmap-container") as HTMLElement),
                heatValues,
                mapCoords,
                imageWidth,
                imageHeight,
                max
            );
            if (tempHeatMap) {
                heatMapOverlay = tempHeatMap
                tempHeatMap.addTo(mapInstance.current)
            }
        } catch (err) {
            console.log(err);
        }
    };

    const showGenomeHeatPoint = (e: React.ChangeEvent<HTMLInputElement>, geneListId: string, geneName: string) => {
        try {
            handleAnnoCheckbox(e.target.id);
            // clear 'cancer' type check
            userAnnoRef.current = false;
            clearMapLayers();
            if (!e.target.checked) return;

            const geoJsonData: GeoJSON = { type: "FeatureCollection", features: [] };
            const geneIndex = genomicData[geneListId].gene_name.findIndex((i) => i === geneName);
            for (let i = 0; i < genomicData[geneListId].grid.length; i++) {
                const latlng = mapCoords.unproject([genomicData[geneListId].grid[i][0], genomicData[geneListId].grid[i][1]]); // Test
                const color = getHeatColor(
                    genomicData[geneListId].spatial_gene_expression[geneIndex].heat_values[i],
                    genomicData[geneListId].spatial_gene_expression[geneIndex].max_value
                );
                geoJsonData.features.push({
                    type: "Feature",
                    properties: { color: color, coordinates: [] },
                    geometry: { type: "Point", coordinates: [latlng.lat, latlng.lng] },
                });
            }
            createWebGLPolygon(geoJsonData, "point");
        } catch (err) {
            console.log(err);
        }
    };

    // eslint-disable-next-line
    const renderHeatPoints = (data: HeatMaps) => {
        const geoJsonData: GeoJSON = { type: "FeatureCollection", features: [] }
        for (let i = 0; i < data.points.length; i++) {
            const latlng = mapCoords.unproject([data.points[i].x, data.points[i].y])
            const color = getHeatColor(data.points[i].value, data.max_value)
            geoJsonData.features.push({
                type: "Feature",
                properties: {color: color, coordinates: []},
                geometry: {type: "Point", coordinates: [latlng.lat, latlng.lng]}
            })
        }
        createWebGLPolygon(geoJsonData, "point")
    }

    const renderJsonHeatmap = async (show: boolean, fileName: string, heatmapName: string) => {
        try {
            // clear polygons on map
            clearMapLayers()
            // clear xml checks
            setXmlClassFilter({})
            setXmlNames([])
            // clear system checks
            setSysClassFilter([])
            // clear user checks
            setUserFilter([])
            // clear bcr heat types
            setBCRClassFilter([])
            // clear 'cancer' type check
            userAnnoRef.current = false
            
            if (!show) {
                if (heatmapName.length) setJsonName(fileName)
                else setJsonName('')
                setJsonClassFilter('')
                return
            }

            if (!(fileName in heatmapData)) {
                const res = await getJsonFile(fileName.split("/").slice(1).join("/"), fileName.split("/")[0])
                if (res.heatmaps) {
                    // renderHeatPoints(res.heatmaps[0])
                    const fixedData = processJsonHeatmap(res.heatmaps, imageWidth, imageHeight)
                    heatmapData[fileName] = fixedData
                    setJsonClasses(prevState => ({
                        ...prevState,
                        [fileName]: res.heatmaps.map((i: any, index: number) => ({id: index, name: i.name}))
                    }))
                }
            }

            if (fileName in heatmapData) {
                if (heatmapName in heatmapData[fileName]) {
                    const tempHeatMap = drawHeatMap(
                        (document.getElementById("heatmap-container") as HTMLElement),
                        heatmapData[fileName][heatmapName].points,
                        mapCoords,
                        imageWidth,
                        imageHeight,
                        heatmapData[fileName][heatmapName].max_value
                    );
                    if (tempHeatMap) {
                        heatMapOverlay = tempHeatMap
                        tempHeatMap.addTo(mapInstance.current)
                    }
                }
            }

            setJsonName(fileName)
            setJsonClassFilter(heatmapName)
        } catch (err) {
            console.log(err)
        }
    }

    const MemoizedVersionsList = useMemo(() =>
        <Versions
            imageData={imageData!}
            activeVerId={activeVerId}
            sysMarkerVer={sysMarkerVer}
            versions={versions}
            isMarkerLoading={isMarkersLoading}
            handleVersionChange={handleVersionChange}
        />
    , [activeVerId, versions, imageData, sysMarkerVer, isMarkersLoading])

    const MemoizedClassList = useMemo(() =>
        <AnnotationClassModal
            addNewTag={addNewTag}
            handleNewTagClose={handleNewTagClose}
            colorLib={colorLib}
            updateFromAnnoClassLibrary={updateFromAnnoClassLibrary}
            removeFromAnnoClassLibrary={removeFromAnnoClassLibrary}
        />
    , [addNewTag, colorLib])  // eslint-disable-line

    return (
        <ViewerContext.Provider value={{
            mapCoords, colorLib, xmlClasses, jsonClasses, xmlNames, xmlClassFilter, jsonName, jsonClassFilter, resetMap, 
            sysClassFilter, userFilter, BCRScore, imageWidth, imageHeight, imageZoomLvl, bcrClassFilter, readonly,
            toggleLoading
        }}>
            <Box className="position-relative d-flex w-100 gap-1 map-height">
                {imageError
                    ? <MapWarningTexts text={"Slide not found"} />
                    : imageData
                        ? imageData.status.toLowerCase() === "delete"
                            ? <MapWarningTexts text={"Slide is marked for deletion"} />
                            : mapError
                                ? <Box className="d-flex flex-column gap-3 align-items-center justify-content-center flex-grow-1">
                                    <Typography variant="h5" component="h1">The viewer failed to initialize. Please try again</Typography>
                                    <Button variant="contained" onClick={() => resetMap()}>Reload</Button>
                                </Box>
                                :  <Box sx={{
                                    position: "relative",
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "space-between",
                                    flexGrow: 1,
                                }}>
                                    <Box sx={{
                                        position: "absolute",
                                        top: 16,
                                        alignSelf: "center",
                                        zIndex: 999,
                                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                                        backdropFilter: "blur(8px)",
                                        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                                        borderRadius: "12px",
                                        px: 2,
                                        pt: 0.5,
                                        pb: 0.25,
                                    }}>
                                        <Typography 
                                            component="h1" 
                                            variant="caption"
                                            sx={{
                                                display: "block",
                                                maxWidth: "40vw",
                                                color: "text.primary",
                                                fontWeight: 400,
                                                letterSpacing: "0.5px",
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis"
                                            }}
                                        >
                                            {imageData.name.split("/").pop()}
                                        </Typography>
                                    </Box>

                                    <Box onContextMenu={handleMapContextEvnt} onWheel={handleMapWheelEvnt}>
                                        <Tooltip
                                            open={showLiveMeasurement}
                                            id="live-measurement"
                                            title="0 mm"
                                            placement="top-start"
                                            followCursor
                                        >
                                            <div ref={mapRef} className="map-style"></div>
                                        </Tooltip>
                                        <div id="heatmap-container" style={{ position: "absolute", display: "none" }}/>
                                    </Box>

                                    <Box className="w-100 d-flex justify-content-between align-items-end gap-5 p-3" sx={{
                                        position: 'absolute',
                                        zIndex: 999,
                                        bottom: 0,
                                        right: 0
                                    }}>
                                        {MemoizedVersionsList}
                                        <Box className="d-flex flex-column align-items-end justify-content-end gap-3 px-3 py-2" sx={{
                                            backgroundColor: "rgba(255, 255, 255, 0.9)",
                                            backdropFilter: "blur(8px)",
                                            border: '2px solid #ccc',
                                            borderRadius: 2,
                                        }}>
                                            {tileInput && (
                                                <Box
                                                    component="form"
                                                    className="d-flex flex-column gap-1 mb-1"
                                                    sx={{ width: 100, mb: 1 }}
                                                    noValidate
                                                    autoComplete="off"
                                                >
                                                    <TextField
                                                        sx={{ bgcolor: "white" }}
                                                        onChange={handleTileSearch}
                                                        name="x"
                                                        hiddenLabel
                                                        variant="filled"
                                                        size="small"
                                                        placeholder="X"
                                                    />
                                                    <TextField
                                                        sx={{ bgcolor: "white" }}
                                                        onChange={handleTileSearch}
                                                        name="y"
                                                        hiddenLabel
                                                        variant="filled"
                                                        size="small"
                                                        placeholder="Y"
                                                    />
                                                    <Button variant="contained" onClick={searchTileLoc} disabled={!(tileInputCoord.x && tileInputCoord.y)}>
                                                        View
                                                    </Button>
                                                </Box>
                                            )}
                                            <Box sx={{
                                                display: 'flex',
                                                gap: '0.75rem',
                                                alignItems: 'center',
                                                color: 'var(--toolgrey)'
                                            }}>
                                                {showMapCoords && <Typography variant="caption" id="coord_indctr_val"></Typography>}
                                                <IconButton
                                                    sx={{cursor: "pointer", "& :hover": {color: "#0d6efd"}, padding: 0}}
                                                    onClick={() => setShowMapCoords(prevState => !prevState)}
                                                >
                                                    {showMapCoords ? <GrFormView /> : <GrFormViewHide />}
                                                </IconButton>
                                                <IconButton
                                                    sx={{cursor: "pointer", "& :hover": {color: "#0d6efd"}, padding: 0}}
                                                    onClick={locateTile}
                                                >
                                                    {tileInput ? <MdOutlineMyLocation /> : <MdLocationSearching />}
                                                </IconButton>
                                            </Box>
                                        </Box>
                                    </Box>

                                    <IconButton
                                        onClick={openDrawer}
                                        aria-label="Open sidebar"
                                        sx={{
                                            position: "absolute",
                                            top: "1rem",
                                            right: "1rem",
                                            zIndex: 999,
                                            borderRadius: 2,
                                            backgroundColor: "rgba(255, 255, 255, 0.9)",
                                            backdropFilter: "blur(8px)",
                                            border: '2px solid #ccc'
                                        }}
                                    >
                                        <FaChevronLeft size={20} />
                                    </IconButton>
                                    <ImageSideBar
                                        drawerOpen={drawerOpen}
                                        closeDrawer={closeDrawer}
                                        imageData={imageData}
                                        gsPredictionResult={gsPredictionResult}
                                        updatePreditionResult={updatePreditionResult}
                                        sysGsAnnotations={sysGsAnnotations}
                                        showSysAnno={showSysAnno}
                                        showByCategory={showByCategory}
                                        modifiedGsAnnotations={modifiedGsAnnotations}
                                        showUserAnno={showUserAnno}
                                        showByUser={showByUser}
                                        userAnnotationCancer={userAnnotationCancer}
                                        bcrAnnotations={bcrAnnotations}
                                        processBCRHeatmap={processBCRHeatmap}
                                        showHeatMap={showHeatMap}
                                        updateBCRScore={updateBCRScore}
                                        zoomToPolygon={zoomToPolygon}
                                        showJsonTileOverlay={showJsonTileOverlay}
                                        point2Tile={point2Tile}
                                        getGenomeData={getGenomeData}
                                        showGenomeHeatMap={showGenomeHeatMap}
                                        showGenomeHeatPoint={showGenomeHeatPoint}
                                        renderJsonHeatmap={renderJsonHeatmap}
                                        setXmlNames={setXmlNames}
                                        setXmlClassFilter={setXmlClassFilter}
                                        showHeatMapByClass={showHeatMapByClass}
                                        sysAnnoLoader={sysAnnoLoader}
                                        userAnnoLoader={userAnnoLoader}
                                    />

                                    <Modal open={serverError.length > 0} onClose={closeServerError}>
                                        <Box sx={modalStyle}>
                                            <Alert severity="error">Failed to process the request {serverError}</Alert>
                                        </Box>
                                    </Modal>

                                    <Menu
                                        onContextMenu={handleMenuContextEvent}
                                        open={contextMenuPos !== null}
                                        onClose={closeMapContextMenu}
                                        anchorReference={contextMenuPos ? "anchorPosition" : "none"}
                                        anchorPosition={contextMenuPos
                                            ? { top: contextMenuPos.mouseY, left: contextMenuPos.mouseX }
                                            : { top: 0, left: 0 }
                                        }
                                    >
                                        {copiedMarkerList.length > 0 && <MenuItem onClick={handlePasteAnno}>Paste</MenuItem>}
                                        {isMarkerSelected && !multiSelect && selectedMarker && selectedMarker.properties.type==='polygon' &&
                                            <MenuItem onClick={handleCopyAnno}>Copy</MenuItem>
                                        }
                                        {isMarkerSelected && !multiSelect && selectedMarker && <MenuItem onClick={handleBringDownMarker}>Bring down</MenuItem>}
                                        {isMarkerSelected && selectedMarker && selectedMarker.properties.type==='polygon' &&
                                            // (multiSelect ? multiSelectList.findIndex(i => i?.properties.id === selectedMarker.properties.id) !== -1 : true) &&
                                            (multiSelect ? selectedLayerById.get(String(selectedMarker.properties.id)) : true) &&
                                            <MenuItem onClick={handleShowClassList}>
                                                Change class to
                                                <Menu
                                                    open={showClassMenu}
                                                    onClose={closeMapContextMenu}
                                                    anchorReference={contextMenuPos ? "anchorPosition" : "none"}
                                                    anchorPosition={contextMenuPos
                                                        ? { top: contextMenuPos.mouseY + 5, left: contextMenuPos.mouseX + 143 }
                                                        : { top: 0, left: 0 }
                                                    }
                                                >
                                                    {categoryListItems()}
                                                </Menu>
                                            </MenuItem>
                                        }
                                        {isMarkerSelected && selectedMarker &&
                                            // (multiSelect ? multiSelectList.findIndex(i => i?.properties.id === selectedMarker.properties.id)!==-1 : true) &&
                                            (multiSelect ? selectedLayerById.get(String(selectedMarker.properties.id)) : true) &&
                                            <MenuItem onClick={contextMenuDeleteHandler}>Delete</MenuItem>
                                        }
                                        <MenuItem onClick={addNewHandler}>Class list</MenuItem>
                                    </Menu>

                                    {/* Filter popup */}
                                    <Box sx={{position: "absolute", bottom: "5rem", display: showSlideFilters ? "flex" : "none", zIndex: 999, alignSelf: "center"}}>
                                        <Paper sx={{display: "flex", flexDirection: "column", width: 300, gap: "1rem", padding: "2rem"}} elevation={3}>
                                            <Box>
                                                <Typography gutterBottom>Brightness</Typography>
                                                <Slider value={brightnessValue} step={10} min={0} max={200} marks aria-label="brightness slider" onChange={adjustBrightness}/>
                                            </Box>
                                            <Box>
                                                <Typography gutterBottom>Contrast</Typography>
                                                <Slider value={contrastValue} step={10} min={0} max={200} marks aria-label="contrast slider" onChange={adjustContrast}/>
                                            </Box>
                                        </Paper>
                                    </Box>

                                    {/* Map contextmenu */}
                                    {MemoizedClassList}

                                    {/* Delete marker popup */}
                                    <Modal open={deleteModal} onClose={closeDeleteModal}>
                                        <Box sx={modalStyle}>
                                            <Box className="d-flex flex-column gap-4">
                                                <Typography variant="h5" component="h1">Delete selected</Typography>
                                                <Typography>
                                                    Confirm to remove the{" "}
                                                    {!multiSelect
                                                        ? selectedMarker
                                                            ? ["line", "pin"].includes(selectedMarker.properties.type.toLowerCase())
                                                                ? selectedMarker.properties.type
                                                                : selectedMarker.properties.category.toLowerCase() + " annotation"
                                                            : ""
                                                        : "selected annotations"
                                                    }
                                                </Typography>
                                                <Box className="d-flex gap-3 justify-content-end">
                                                    {waitingForRes
                                                        ? <CircularProgress size={28} />
                                                        : <>
                                                            <Button variant="outlined" onClick={closeDeleteModal} disabled={waitingForRes}>Cancel</Button>
                                                            <Button
                                                                variant="contained"
                                                                color="error"
                                                                onClick={() => multiSelect ? removeMarkerList() : removeMarker()}
                                                                disabled={waitingForRes}
                                                            >
                                                                Confirm
                                                            </Button>
                                                        </>
                                                    }
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Modal>

                                    {tileMsg &&
                                        <Snackbar
                                            open={tileMsg ? true : false}
                                            autoHideDuration={5000}
                                            onClose={handleCloseTileMsg}
                                            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                                        >
                                            <Alert onClose={handleCloseTileMsg} severity={tileMsg!.type} sx={{ width: "100%" }}>
                                                {tileMsg!.message}
                                            </Alert>
                                        </Snackbar>
                                    }
                                </Box>
                        : null
                }
                {/* 3 dots wave animation on center */}
                <div id="marker-tooltip" className="position-absolute p-3 marker-popup" style={{ zIndex: 999, display: "none" }}></div>
                {isLoading &&
                    <Box className="position-absolute w-100 h-100" sx={{ zIndex: 999, background: 'white' }}>
                        <LoadingAnimeSpinner />
                    </Box>
                }
                {/* bottom line loading animation */}
                {(bcrHeatmapLoading || isMarkersLoading || sysAnnoLoader) &&
                    <Box className='position-absolute w-100' sx={{zIndex: 999, bottom: 0, left: 0}}>
                        <LinearProgress/>
                    </Box>
                }
                {/* bottom center text notification */}
                <Snackbar
                    open={Boolean(toast.length > 1)}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center'
                    }}
                    message={toast}
                    action={<>
                            <IconButton
                                size="small"
                                aria-label="close"
                                color="inherit"
                                onClick={() => setToast('')}
                            >
                                <MdOutlineClose />
                            </IconButton>
                        </>
                    }
                />
            </Box>
        </ViewerContext.Provider>
    );
};

export default ImageViewer;